import type { MobileOS } from "src/hooks/use-mobile-os";

export type AppBannerEligibility =
  | { eligible: true; mobileOS: MobileOS }
  | { eligible: false; reason: AppBannerIneligibleReason };

export type AppBannerIneligibleReason =
  | "app_webview"
  | "not_mobile"
  | "ios_safari"
  | "excluded_route"
  | "first_visit"
  | "used_up";

/** The banner shows once the shopper is this far down the page (0 to 1). */
export const APP_BANNER_SCROLL_PROGRESS = 0.5;

/**
 * Page templates (Next's `router.pathname`) where the banner never shows, with
 * everything under them: the PDP, cart and every checkout step keep their own
 * calls to action clear, the order confirmation page already offers the app
 * stores, and sign-in and the apps' own pages are not for browsing.
 */
const EXCLUDED_ROUTES = [
  "/product",
  "/cart",
  "/checkout",
  "/app",
  "/app-login",
  "/sign-in-or-register",
] as const;

// Browsers on iOS that put their own name in the user agent. Safari's has none
// of these; in-app browsers (Instagram, Facebook) and Google's app count as
// other browsers, so the banner shows in them.
const NON_SAFARI_IOS_MARKERS = [
  "CriOS",
  "FxiOS",
  "EdgiOS",
  "OPiOS",
  "OPT/",
  "DuckDuckGo",
  "YaBrowser",
  "GSA/",
  "Instagram",
  "FBAN",
  "FBAV",
  "Snapchat",
  "Pinterest",
  "TikTok",
  "musical_ly",
  "LinkedInApp",
  "Line/",
] as const;

/**
 * Whether the browser is Safari on an iPhone or iPad, which shows its own app
 * banner at the top of the page. Every browser on iOS runs on WebKit, so only
 * the user agent tells them apart; `isSafari()` in src/utils recognises
 * desktop Safari by its push API, which no iOS browser has.
 */
export function isSafariOnIos({
  mobileOS,
  userAgent,
}: {
  mobileOS: MobileOS | undefined;
  userAgent: string;
}): boolean {
  if (mobileOS !== "ios" || !/Safari\//.test(userAgent)) {
    return false;
  }

  return !NON_SAFARI_IOS_MARKERS.some((marker) => userAgent.includes(marker));
}

export function isExcludedAppBannerRoute(pathname: string): boolean {
  return EXCLUDED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );
}

/**
 * Whether the banner may appear on this page for this device, checked in the
 * order listed; the scroll depth is checked separately, as the shopper scrolls.
 */
export function getAppBannerEligibility({
  isAppWebview,
  mobileOS,
  userAgent,
  pathname,
  visit,
  isUsedUp,
}: {
  isAppWebview: boolean;
  /** From `detectMobileOS()` in src/hooks/use-mobile-os.ts. */
  mobileOS: MobileOS | undefined;
  userAgent: string;
  pathname: string;
  visit: number;
  isUsedUp: boolean;
}): AppBannerEligibility {
  if (isAppWebview) {
    return { eligible: false, reason: "app_webview" };
  }

  if (!mobileOS) {
    return { eligible: false, reason: "not_mobile" };
  }

  if (isSafariOnIos({ mobileOS, userAgent })) {
    return { eligible: false, reason: "ios_safari" };
  }

  if (isExcludedAppBannerRoute(pathname)) {
    return { eligible: false, reason: "excluded_route" };
  }

  if (visit < 2) {
    return { eligible: false, reason: "first_visit" };
  }

  if (isUsedUp) {
    return { eligible: false, reason: "used_up" };
  }

  return { eligible: true, mobileOS };
}
